import type {
  AccessPolicyUpdate,
  EffortAccessMap,
  EffortLevel,
  EffortPolicy,
} from "@/api/admin-api"

import { EFFORT_LEVELS } from "@/api/admin-api"

import { isPolicyConfigured } from "../utils/access-policy"

// Pure decision logic for EffortAccessDialog (#2667), pulled out of the
// component so the row-save/toggle math — which has repeatedly hidden bugs
// only a live click-through caught — has direct unit coverage instead.

// A row counts as "has its own entry" when it carries an ACL restriction/open
// override OR an explicit disabled quick-toggle (true OR false — an explicit
// `disabled: false` is how a per-model row reopens a level the baseline
// disabled, so its presence alone counts as an override, same as the backend's
// `"disabled" in model_policy` check) — either one must survive a whole-map
// save even when a *different* row is the one being edited.
export function isRowConfigured(policy: EffortPolicy | undefined): boolean {
  return isPolicyConfigured(policy) || (policy !== undefined && "disabled" in policy)
}

export function clearDisabledFlag(policy: EffortPolicy): EffortPolicy {
  const rest: EffortPolicy = { ...policy }
  delete rest.disabled
  return rest
}

/** Replace one row in the map with `rowPolicy`, dropping any row (including
 * others) that isn't configured — the whole-map PUT contract. */
export function buildSaveMap(
  map: EffortAccessMap,
  effort: EffortLevel,
  rowPolicy: EffortPolicy | undefined
): EffortAccessMap {
  const next: EffortAccessMap = {}
  for (const level of EFFORT_LEVELS) {
    const policy = level === effort ? rowPolicy : map[level]
    if (isRowConfigured(policy)) next[level] = policy
  }
  return next
}

/** Save the ACL sub-dialog's result for one row, preserving that row's own
 * disabled flag (present at all — true or an explicit false reopen) across
 * the save regardless of whether the ACL portion ends up configured —
 * otherwise saving an empty/no-op ACL edit on a disabled row would silently
 * re-enable it. */
export function computeAclSaveMap(
  map: EffortAccessMap,
  effort: EffortLevel,
  policy: AccessPolicyUpdate | undefined
): EffortAccessMap {
  const currentDisabled = map[effort]?.disabled
  const aclConfigured = policy !== undefined && isPolicyConfigured(policy)
  const rowPolicy: EffortPolicy | undefined =
    aclConfigured || currentDisabled !== undefined
      ? {
          ...(aclConfigured ? policy : {}),
          ...(currentDisabled !== undefined ? { disabled: currentDisabled } : {}),
        }
      : undefined
  return buildSaveMap(map, effort, rowPolicy)
}

/** Compute the save-map for the quick enable/disable switch.
 *
 * Disabling always writes an explicit `disabled: true` on this row.
 *
 * Enabling is the subtle direction: clearing this row's own disabled flag is
 * only safe when the level ends up genuinely available afterwards. If the
 * row has no other ACL content AND the baseline still disables this level,
 * deleting the key would silently fall through to that still-disabled
 * baseline — instead we must persist an explicit `disabled: false` reopen.
 * This check runs on *every* enable, not just when the row starts with no
 * entry at all: after one off→on→off cycle the row's own entry is
 * `{disabled: true}` — still "has an own entry" — so a check gated on
 * "no own entry" would miss the second enable and the toggle would appear to
 * work once and then silently stop working.
 */
export function computeToggleDisabledSaveMap(args: {
  map: EffortAccessMap
  inheritMap: EffortAccessMap | undefined
  effort: EffortLevel
  nextEnabled: boolean
}): EffortAccessMap {
  const { map, inheritMap, effort, nextEnabled } = args
  const current = map[effort]
  let rowPolicy: EffortPolicy | undefined
  if (nextEnabled) {
    const rest = current ? clearDisabledFlag(current) : undefined
    const clearedRowHasContent = rest !== undefined && isPolicyConfigured(rest)
    const baselineStillDisabled = !clearedRowHasContent && inheritMap?.[effort]?.disabled === true
    if (baselineStillDisabled) {
      rowPolicy = { ...(rest ?? {}), disabled: false }
    } else {
      rowPolicy = clearedRowHasContent ? rest : undefined
    }
  } else {
    rowPolicy = { ...(current ?? {}), disabled: true }
  }
  return buildSaveMap(map, effort, rowPolicy)
}

export type EffortRowState = {
  isDisabled: boolean
  isInheritedDisabled: boolean
  isExplicitlyOpen: boolean
  isRestricted: boolean
  inheritsConfigured: boolean
}

/** Derive display state for one row — own vs. inherited disable, explicit
 * open override, ACL restriction, or plain baseline inheritance. */
export function computeRowState(
  map: EffortAccessMap,
  inheritMap: EffortAccessMap | undefined,
  effort: EffortLevel
): EffortRowState {
  const rowPolicy = map[effort]
  const hasOwnEntry = isRowConfigured(rowPolicy)
  const ownDisabled = rowPolicy?.disabled === true
  // A row with no override of its own inherits the baseline's disabled state
  // too — surfacing that here (rather than "Available to everyone") is what
  // actually governs whether this model can use the effort, per the
  // backend's override-wins-over-base layering.
  const inheritedDisabled = !hasOwnEntry && inheritMap?.[effort]?.disabled === true
  const isDisabled = ownDisabled || inheritedDisabled
  const configured = isPolicyConfigured(rowPolicy)
  const isExplicitlyOpen = rowPolicy?.unrestricted === true
  const isRestricted = configured && !isExplicitlyOpen
  const inheritsConfigured = !configured && !isDisabled && isPolicyConfigured(inheritMap?.[effort])
  return {
    isDisabled,
    isInheritedDisabled: inheritedDisabled,
    isExplicitlyOpen,
    isRestricted,
    inheritsConfigured,
  }
}
